/**
 * SafetyTech section — highlights the Cautio dashcam ecosystem powering
 * in-cabin safety on every Sarathi-X ride. Mirrors the visual rhythm of
 * FleetSection: uppercase eyebrow → h2 → subtitle → feature grid → CTA.
 * Sits between FleetSection and AppDownloadSection on the homepage.
 */
import { motion } from "framer-motion";
import { Video, MapPin, Mic, ArrowUpRight, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";

const SafetyTechSection = () => {
  // Mirror the marketing copy from cautio.com — keep terse and concrete.
  const features = [
    {
      icon: Video,
      title: "AI Vision System",
      lines: [
        "Intelligent road and cabin awareness for safer rides.",
        "Consent based HD recording for safety and transparency.",
      ],
    },
    {
      icon: MapPin,
      title: "Live Journey Intelligence",
      lines: ["Real-time vehicle insights throughout your trip."],
    },
    {
      icon: Mic,
      title: "Driver Connect",
      lines: ["Instant communication with the Cautio", "command centre."],
      note: "(Launching soon)",
    },
  ];

  return (
    <section
      id="safety-tech"
      className="py-12 sm:py-16 lg:py-20 bg-sarathi-background"
      data-testid="safety-tech-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-10"
        >
          <span className="text-sarathi-primary font-semibold text-[10px] sm:text-xs tracking-widest uppercase">
            Vehicle Intelligence
          </span>
          <h2 className="font-outfit text-2xl sm:text-3xl font-bold tracking-tight text-white mt-2 sm:mt-3 mb-2 sm:mb-3">
            Powered by Cautio
          </h2>
          <p className="text-sarathi-text-secondary text-sm max-w-xl mx-auto px-2 leading-relaxed">
            Every Sarathi-X vehicle is enhanced with Cautio's AI safety ecosystem<br />
            protecting every journey in real time.
          </p>
        </motion.div>

        {/* Feature grid — 1 col on phones, 3 col on tablet+ */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5">
          {features.map((f, idx) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="bg-sarathi-surface border border-white/[0.06] rounded-2xl p-4 sm:p-6 hover:border-sarathi-primary/30 transition-colors"
                data-testid={`safety-feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-sarathi-primary/10 border border-sarathi-primary/20 flex items-center justify-center mb-3 sm:mb-4">
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-sarathi-primary" />
                </div>
                <h3 className="font-outfit text-base font-semibold text-white mb-1.5">
                  {f.title}
                </h3>
                <p className="text-sarathi-text-secondary text-sm leading-relaxed">
                  {f.lines.map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}
                  {f.note && (
                    <span
                      className="block italic font-bold text-sarathi-primary mt-1"
                      data-testid={`safety-feature-note-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {f.note}
                    </span>
                  )}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Footnote with link to Cautio + the customer-facing Dashcam FAQ */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-7 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6"
        >
          <a
            href="https://www.cautio.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sarathi-text-muted hover:text-sarathi-primary text-xs sm:text-sm transition-colors"
            data-testid="cautio-link"
          >
            Learn more about Cautio
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
          <span className="hidden sm:inline text-sarathi-text-muted/40 text-xs">·</span>
          <Link
            to="/dashcam-faqs"
            className="inline-flex items-center gap-1.5 text-sarathi-text-muted hover:text-sarathi-primary text-xs sm:text-sm transition-colors"
            data-testid="dashcam-faqs-link"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            DASHCAM FAQs
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default SafetyTechSection;
